import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError } from './api'

export type Resource<T> = {
  data: T | null
  error: ApiError | Error | null
  loading: boolean
  /** True when the failure was a 403 — the screen should say "not permitted",
   *  not "something went wrong". */
  forbidden: boolean
  reload: () => void
}

/**
 * Fetch-on-mount with abort, re-run on dependency change, and a manual reload.
 *
 * Deliberately not a caching layer. An admin console showing a stale suspension
 * state or a stale spend figure is worse than one that refetches — every screen
 * here is read-then-act.
 */
export function useResource<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: unknown[] = [],
): Resource<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<ApiError | Error | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)

    void (async () => {
      try {
        const result = await fetcherRef.current(controller.signal)
        if (!controller.signal.aborted) setData(result)
      } catch (caught) {
        if (controller.signal.aborted) return
        if (caught instanceof DOMException && caught.name === 'AbortError') return
        setError(caught instanceof Error ? caught : new Error(String(caught)))
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    })()

    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  return {
    data,
    error,
    loading,
    forbidden: error instanceof ApiError && error.isForbidden,
    reload,
  }
}
